/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

// ============================================================================
// SUPABASE CLIENT CONFIGURATION - Clean Architecture
// ============================================================================

import { logger } from "@/lib/utils/logger";
import type { Database } from "@/types";
import { createClient as createSupabaseClient } from "@supabase/supabase-js";
import { getSupabaseConfig } from "./config";

// ============================================================================
// ENVIRONMENT VARIABLES
// ============================================================================

// Lazy config initialization to prevent build/test-time failures
let config: ReturnType<typeof getSupabaseConfig> | null = null;

const getConfig = () => {
  if (!config) {
    config = getSupabaseConfig();
  }
  return config;
};

// ============================================================================
// SUPABASE CLIENT INSTANCE
// ============================================================================

// Global singleton instance to prevent multiple GoTrueClient instances
let supabaseInstance:
  | ReturnType<typeof createSupabaseClient<Database>>
  | undefined;

export const getSupabaseClient = (): ReturnType<
  typeof createSupabaseClient<Database>
> => {
  // In the browser, always prefer the Clerk-authenticated singleton if present
  if (typeof window !== "undefined") {
    const clerkClient = window.__ECP_SUPABASE_CLERK_CLIENT__ as
      | ReturnType<typeof createSupabaseClient<Database>>
      | undefined;
    if (clerkClient && supabaseInstance !== clerkClient) {
      supabaseInstance = clerkClient;
    }
  }

  if (!supabaseInstance) {
    const cfg = getConfig();
    // Avoid noisy logs in the browser; only log on the server
    if (typeof window === "undefined") {
      logger.info(
        `🔧 Creating Supabase client for ${cfg.environment} environment`,
      );
      logger.info(`📍 Supabase URL: ${cfg.url}`);
    }
    supabaseInstance = createSupabaseClient<Database>(cfg.url, cfg.anonKey, {
      // Use 2025 native Clerk-Supabase integration
      async accessToken() {
        if (typeof window !== "undefined") {
          try {
            // Use Clerk's default JWT token (native integration)
            const getToken = window?.Clerk?.session?.getToken;
            if (typeof getToken === "function") {
              return (await getToken()) ?? null;
            }
            return null;
          } catch (error) {
            logger.warn("Failed to get Clerk token", {
              error: error instanceof Error ? error.message : String(error),
            });
            return null;
          }
        }
        return null;
      },
      auth: {
        // When paired with Clerk, do not persist Supabase sessions in the browser
        autoRefreshToken: false,
        persistSession: false,
        detectSessionInUrl: false,
        storageKey: "ecp-public",
      },
    });
    // Expose as window singleton so other creators reuse instead of creating another client
    if (typeof window !== "undefined") {
      if (!window.__ECP_SUPABASE_CLERK_CLIENT__) {
        window.__ECP_SUPABASE_CLERK_CLIENT__ = supabaseInstance as unknown;
      }
    }
  }
  return supabaseInstance;
};

// Export createClient for backward compatibility (do not eagerly instantiate)
export const createClient = createSupabaseClient;

// ============================================================================
// TYPE HELPERS
// ============================================================================

export type Tables = Database["public"]["Tables"];
export type Views = Database["public"]["Views"];

// ============================================================================
// ERROR HANDLING
// ============================================================================

export interface SupabaseError {
  code?: string;
  message?: string;
}

/**
 * Returns the Supabase client without Database generic typing.
 * Use for queries with nested relation joins (e.g. `select("*, project:projects(*)")`)
 * that exceed TypeScript's type instantiation depth limit.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getUntypedSupabaseClient = (): any => getSupabaseClient();

export const handleSupabaseError = (error: unknown): { error: string } => {
  if (error && typeof error === "object" && "message" in error) {
    return { error: (error as { message: string }).message };
  }
  return { error: "An unknown error occurred" };
};
